import actionTypes from 'redux/actions/actionTypes'
import entityTypes from 'helpers/enums/entityTypes'
import { localize, localizeWithVars } from 'helpers/translator/localize'
import errorCodes from 'helpers/enums/errorCodes'
import localErrorCodes from 'helpers/enums/errors/localErrorCodes'
import topInformationTypes from 'helpers/enums/topInformationTypes'
import videoStatusTypes from 'helpers/enums/interactiveVideoCreation/videoStatusTypes'

function openInformation(state, action) {
	let informationExists = false

	if (action.messageObject.actionType) {
		informationExists =
			state.array.findIndex(information => information.actionType === action.messageObject.actionType) !== -1
	}

	if (!informationExists) {
		const uid = state.lastId + 1
		return {
			lastId: uid,
			array: [...state.array, {...action.messageObject, uid}],
		}
	} else {
		return state
	}
}

function closeInformation(state, uid) {
	const index = state.array.findIndex(information => information.uid === uid)
	if (index !== -1) {
		return {
			lastId: state.lastId,
			array: [...state.array.slice(0, index), ...state.array.slice(index + 1)],
		}
	} else {
		return state
	}
}

function showBindersSavedMessage(state) {

	return openInformation(state, {
		messageObject: {
			message: localize('private_library_cart_itemRemoved'),
			styleType: 'success',
		},
	})
}

function showForgotPasswordInformation(state, action) {
	if (action.shouldShowInformation) {
		return openInformation(state, {
			messageObject: {
				message: localize('google_slides_refresh'),
				styleType: 'neutral',
				timeout: 1000000000,
				delay: 3000,
			},
		})
	}

	return state
}

function closeLastOpenInformation(state, action) {
	const newState = closeInformation(state, state.lastId)
	return openInformation(newState, action)
}

function handleGoogleSlidesSaveFailed(state, action) {
	if (action.error.error_code === errorCodes.GOOGLE_SLIDES_SAVE_ALREADY_PUBLISHED) {
		return openInformation(state, {
			messageObject: {
				message: localize('googleSlides_savePresentationFailed_alreadyPublished'),
				styleType: 'error',
			},
		})
	} else {
		return openInformation(state, {
			messageObject: {
				message: localize('googleSlides_savePresentationFailed'),
				styleType: 'error',
			},
		})
	}
}

function closeOpenInformationByType(state, type) {
	const filteredTopInformation = state.array.find(topInformation => topInformation.type === type)
	if (filteredTopInformation) {
		return closeInformation(state, filteredTopInformation.uid)
	} else {
		return state
	}
}

function handleInteractiveVideoUploadFailed(state, action) {
	const paywallErrors = [
		errorCodes.EXCEED_MAX_PRESENTATION_SIZE,
		errorCodes.EXCEED_MAX_USER_STORAGE,
	]

	if (action.serverError.error_code === errorCodes.EXCEED_MAX_FILE_SIZE) {
		return openInformation(state, {
			messageObject: {
				type: topInformationTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_FAILED,
				actionType: action.type,
				styleType: 'error',
				message: action.serverError.message,
				showCloseBtn: true,
			},
		})
	}

	if (!paywallErrors.includes(action.serverError.error_code)) {
		return openInformation(state, {
			messageObject: {
				type: topInformationTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_FAILED,
				actionType: action.type,
				styleType: 'error',
				message: localize('interactive_video_upload_error'),
				showCloseBtn: true,
			},
		})
	}

	return state
}

const informationReducer = (state = {lastId: 0, array: []}, action) => {
	switch (action.type) {
		case actionTypes.OPEN_TOP_INFORMATION:
			return openInformation(state, action)
		case actionTypes.CLOSE_TOP_INFORMATION:
			return closeInformation(state, action.uid)
		case actionTypes.PRIVATE_LIBRARY_NAME_FOLDER_SUCCESS:
			const message = action.folderUID === 0 ? localize('private_library_folder_created') : localize('private_library_folder_renamed')
			return openInformation(state, {
				messageObject: {
					message,
					styleType: 'success',
				},
			})
		case actionTypes.PRIVATE_LIBRARY_DELETE_CURRENT_FOLDER_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('private_library_folder_deleted'),
					styleType: 'success',
				},
			})
		case actionTypes.PRIVATE_LIBRARY_FOLDER_ADD_LESSON_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('private_library_folder_lessonAdded'),
					styleType: 'success',
				},
			})
		case actionTypes.PRIVATE_LIBRARY_FOLDER_REMOVE_LESSON_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('private_library_folder_lessonRemoved'),
					styleType: 'success',
				},
			})
		case actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_SUCCESS:
			let messageText
			if (action.multipleLibraries) {
				messageText = action.entity.entityType === entityTypes.BUNDLE ? localize('private_library_bundle_multipleLibraries_deleted') : localize('private_library_lesson_multipleLibraries_deleted')
			} else {
				messageText = action.entity.entityType === entityTypes.BUNDLE ? localize('private_library_bundleDeleted') : localize('private_library_lessonDeleted')
			}
			return openInformation(state, {
				messageObject: {
					message: messageText,
					styleType: 'success',
				},
			})
		case actionTypes.CART_BUY_PRODUCT_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: action.entityType === 'B' ? localize('private_library_bundleAdded') : localize('private_library_lessonAdded'),
					styleType: 'success',
				},
			})
		case actionTypes.CART_ITEM_REMOVED:
			return openInformation(state, {
				messageObject: {
					message: localize('private_library_cart_itemRemoved'),
					styleType: 'success',
				},
			})
		case actionTypes.BINDERS_BINDER_CREATE_NEW:
			return openInformation(state, {
				messageObject: {
					message: localize('binders_message_newBinder'),
					styleType: 'success',
				},
			})
		case actionTypes.BINDERS_SAVE_LESSONS_SUCCESS:
			return showBindersSavedMessage(action, state)
		case actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ENTITIES_SAVED:
			if (action.multipleLibraries) {
				messageText = (action.bundles.length + action.lessons.length) > 1 ? localize('private_library_lessons_added_to_multiple_libraries') : localize('private_library_lesson_added_to_multiple_libraries')
			} else {
				messageText = (action.bundles.length + action.lessons.length) > 1 ? localize('private_library_lessons_added_to_single_library') : localize('private_library_lesson_added_to_single_library')
			}
			return openInformation(state, {
				messageObject: {
					message: messageText,
					styleType: 'success',
				},
			})
		case actionTypes.BINDERS_SAVED_NOTES:
			return openInformation(state, {
				messageObject: {
					message: action.isLast ? localize('binders_message_notesComplete') : localize('binders_message_notesSaved'),
					timeout: 3000,
					styleType: 'success',
				},
			})
		case actionTypes.GOOGLE_SLIDES_AUTHORIZE_USER_FAILED:
			if (action.error.message && !action.error.message.includes('Cookies')) {
				return openInformation(state, {
					messageObject: {
						message: localize('global_generic_error'),
						styleType: 'error',
					},
				})
			} else {
				return state
			}
		case actionTypes.MY_ACCOUNT_DATA_FETCHED:
			if (action.payload.alertMessage && action.payload.alertMessage.message) {
				return openInformation(state, {
					messageObject: {
						message: action.payload.alertMessage.message,
						showCloseBtn: true,
					},
				})
			}
			return state
		case actionTypes.BINDERS_SAVE_FORM_FAIL:
			{/* TRA-KEY */}
			const messageBindersText = action.error_code === 31 ? 'Sub Plans can’t be sent prior to today' : 'Your form has incomplete fields.'
			return openInformation(state, {
				messageObject: {
					message: messageBindersText,
					timeout: 3000,
					styleType: 'error',
				},
			})
		case actionTypes.MY_ACCOUNT_SAVE_PERSONAL_INFO:
		case actionTypes.MY_ACCOUNT_SAVE_SCHOOL_INFO:
		case actionTypes.MY_ACCOUNT_SAVE_TEACHER_INFO:
		case actionTypes.MY_LIBRARY_DOWNLOAD_FINISHED_AND_LAUNCH_STARTED:
			return closeInformation(state, state.lastId)
		case actionTypes.PLAYGROUND_NEARPOD_PICKER_FILE_ERROR:
			{/* TRA-KEY */}
			return openInformation(state, {
				messageObject: {
					message: 'Incompatible file',
					styleType: 'error',
				},
			})
		case actionTypes.MY_ACCOUNT_CHANGE_PASSWORD_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: action.message,
					timeout: 3000,
					styleType: 'success',
				},
			})
		case actionTypes.POLL_SAVE_POLL_ERROR:
		case actionTypes.POLL_QUESTION_UPDATE_ERROR:
		case actionTypes.OEQ_QUESTION_UPDATE_ERROR:
		case actionTypes.OEQ_SAVE_OEQ_ERROR:
		case actionTypes.QUIZ_SAVE_QUIZ_ERROR:
			return openInformation(state, {
				messageObject: {
					actionType: action.type,
					message: action.error.error_code === errorCodes.EXCEED_MAX_CHARS ? localize('global_review_information_error') : localize('global_generic_error'),
					styleType: 'error',
				},
			})
		case actionTypes.POLL_SAVE_SETTINGS_ERROR:
		case actionTypes.POLL_VALIDATION_ERROR:
		case actionTypes.OEQ_VALIDATION_ERROR:
		case actionTypes.POLL_ADD_QUESTION_VALIDATION_ERROR:
		case actionTypes.POLL_ADD_ANSWER_VALIDATION_ERROR:
		case actionTypes.POLL_DELETE_QUESTION_VALIDATION_ERROR:
		case actionTypes.POLL_DELETE_ANSWER_VALIDATION_ERROR:
		case actionTypes.GAME_UPLOAD_QUESTION_MEDIA_DELETE_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_ERROR:
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_POSTER_ERROR:
			return openInformation(state, {
				messageObject: {
					actionType: action.type,
					message: action.message,
					styleType: 'error',
				},
			})
		case actionTypes.DESMOS_VALIDATION_ERROR:
		case actionTypes.DESMOS_SAVE_DESMOS_ERROR:
			return openInformation(state, {
				messageObject: {
					actionType: action.type,
					message: action.message,
					styleType: 'error',
				},
			})
		case actionTypes.POLL_SAVE_SETTINGS_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: action.message,
					styleType: 'success',
				},
			})
		case actionTypes.QUIZ_VALIDATION_ERROR:
		case actionTypes.QUIZ_ADD_QUESTION_VALIDATION_ERROR:
		case actionTypes.QUIZ_ADD_ANSWER_VALIDATION_ERROR:
		case actionTypes.QUIZ_DELETE_QUESTION_VALIDATION_ERROR:
		case actionTypes.QUIZ_DELETE_ANSWER_VALIDATION_ERROR:
			return openInformation(state, {
				messageObject: {
					actionType: action.type,
					message: action.message,
					styleType: 'error',
				},
			})
		case actionTypes.HELPBOX_SEND_HELP_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: action.message,
					styleType: 'success',
				},
			})
		case actionTypes.HELPBOX_SEND_HELP_ERROR:
		case actionTypes.MY_ACCOUNT_UPLOADER_VALIDATE_ERROR:
		case actionTypes.MY_ACCOUNT_UPLOADER_UPLOAD_ERROR:
		case actionTypes.MY_ACCOUNT_UPLOAD_PROFILE_IMAGE_ERROR:
			return openInformation(state, {
				messageObject: {
					message: action.message,
					styleType: 'error',
				},
			})
		case actionTypes.MY_ACCOUNT_SAVE_ICON_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('my_account_profile_image_save_success'),
					styleType: 'success',
				},
			})
		case actionTypes.MY_ACCOUNT_SAVE_ICON_ERROR:
			return openInformation(state, {
				messageObject: {
					message: action.response.message,
					styleType: 'error',
				},
			})
		case actionTypes.STOREFRONT_LESSONS_PICKER_ADD_SUCCESS:
		case actionTypes.STOREFRONT_REMOVE_LESSON_CONFIRMED:
		case actionTypes.STOREFRONT_OWNER_SORT_LESSONS_RELEASE:
		case actionTypes.STOREFRONT_OWNER_BIO_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('storefront_success_update_notification'),
					styleType: 'success',
				},
			})
		case actionTypes.STOREFRONT_VISITOR_FOLLOW_OWNER_SUCCESS:
		case actionTypes.STOREFRONT_VISITOR_UNFOLLOW_OWNER_SUCCESS:
			return openInformation(state, {
				messageObject: {
					actionType: action.type,
					message: action.message,
					styleType: 'success',
				},
			})
		case actionTypes.MATCHING_PAIRS_CT_PAIR_ADD_ANOTHER_SAVE_SUCCESS:
		case actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_SUCCESS:
		case actionTypes.MATCHING_PAIRS_CT_DELETE_PAIR_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: action.message,
					timeout: 3000,
					styleType: 'success',
				},
			})
		case actionTypes.MATCHING_PAIRS_CT_PAIR_SAVE_VALIDATE_ERROR:
		case actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_VALIDATE_ERROR:
		case actionTypes.MATCHING_PAIRS_CT_SAVE_ACTIVITY_ERROR:
			return openInformation(state, {
				messageObject: {
					message: action.data.message,
					timeout: 3000,
					styleType: 'error',
				},
			})
		case actionTypes.DRAW_IT_SAVE_INSTRUCTIONS_ERROR:
		case actionTypes.DRAW_IT_SAVE_DRAW_IT_ERROR:
			return openInformation(state, {
				messageObject: {
					message: action.message,
					styleType: 'error',
				},
			})
		case actionTypes.PRIVATE_LIBRARY_LESSON_ALREADY_ADDED:
			const libraryType = action.libraryType === 'school' ? localize('preview_toolbar_schoolLibrary') : localize('preview_toolbar_districtLibrary')
			const entityType = action.entityType === 'bundle' ? localize('privateLibrary_cantAddBundleMessage') : localize('privateLibrary_cantAddLessonMessage')

			return openInformation(state, {
				messageObject: {
					message: entityType + libraryType,
					styleType: 'error',
				},
			})
		case actionTypes.MY_LIBRARY_ADD_LESSON_TO_PUBLIC_LIBRARY_FAILED:
			let barMessage = localize('my_library_add_lesson_public_library_fail')
			if (action.response.error_code !== 28 && action.response.message) {
				barMessage = action.response.message
			}
			return openInformation(state, {
				messageObject: {
					message: barMessage,
					styleType: 'error',
				},
			})
		case actionTypes.MY_LIBRARY_ADD_LESSON_TO_PUBLIC_LIBRARY_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('my_library_add_lesson_public_library_success'),
					styleType: 'success',
				},
			})
		case actionTypes.MY_LIBRARY_ADD_LESSON_TO_PRIVATE_LIBRARY_SUCCESS:
			const library_type = action.library_type === 'school' ? localize('my_library_school_library') : localize('my_library_district_library')
			return openInformation(state, {
				messageObject: {
					message: localize('my_library_add_lesson_private_library_success') + ' ' + library_type,
					styleType: 'success',
				},
			})
		case actionTypes.MY_LIBRARY_ADD_LESSON_TO_PRIVATE_LIBRARY_FAILED:
		case actionTypes.STORE_ADD_LESSON_TO_PRIVATE_LIBRARY_FAILED:
			if (action.error.error_code === 28) {
				const library_type = action.library_type === 'school' ? localize('my_library_school_library') : localize('my_library_district_library')
				return openInformation(state, {
					messageObject: {
						message: localize('my_library_add_lesson_private_library_error') + ' ' + library_type,
						styleType: 'error',
					},
				})
			}
			return state
		case actionTypes.MY_LIBRARY_ADD_OR_UPDATE_FOLDER_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localizeWithVars('my_library_folder_updated_success_2', {
						folderName: action.foldersData.name,
					}),
					styleType: 'success',
				},
			})
		case actionTypes.MY_LIBRARY_DELETE_FOLDER_RESPONSE_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localizeWithVars('my_library_delete_folder_success_2', {
						folderName: action.folderName,
					}),
					styleType: 'success',
				},
			})
		case actionTypes.MY_LIBRARY_DELETE_FOLDER_RESPONSE_FAILED:
			return openInformation(state, {
				messageObject: {
					message: localize('my_library_delete_folder_fail'),
					styleType: 'error',
				},
			})
		case actionTypes.OVERLAY_SCHOOL_LIBRARY_ONBOARDING_CONFIRM:
			return openInformation(state, {
				messageObject: {
					message: localize('school_onboarding_overlay'),
					styleType: 'success',
				},
			})
		case actionTypes.OVERLAY_DISTRICT_LIBRARY_ONBOARDING_CONFIRM:
			return openInformation(state, {
				messageObject: {
					message: localize('district_onboarding_overlay'),
					styleType: 'success',
				},
			})
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_PICKED:
			return closeOpenInformationByType(
				state,
				topInformationTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_FAILED
			)
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_STATUS_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_FAILED:
			return handleInteractiveVideoUploadFailed(state, action)
		case actionTypes.ADMIN_ONBOARDING_SKIP_FAILED:
		case actionTypes.MY_LIBRARY_SESSION_CODE_SAVE_EXPIRATION_DATE_FAILED:
		case actionTypes.MY_LIBRARY_LTI_SESSION_FAILED:
		case actionTypes.MY_LIBRARY_ADD_OR_UPDATE_FOLDER_FAILED:
		case actionTypes.GOALS_TIME_TO_CLIMB_CARD_ENABLE_LESSON_FAILED:
		case actionTypes.GOALS_OEQ_CARD_CREATE_LESSON_FAILED:
		case actionTypes.GOALS_OEQ_CARD_ENABLE_LESSON_FAILED:
		case actionTypes.QUICK_LAUNCH_OEQ_CREATE_LESSON_FAILED:
		case actionTypes.QUICK_LAUNCH_DRAW_IT_CREATE_LESSON_FAILED:
		case actionTypes.QUICK_LAUNCH_COLLABORATE_CREATE_LESSON_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_CREATE_FAILED:
		case actionTypes.USER_SCHOOL_PROFILE_SAVE_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_ACTIVITY_CREATE_ERROR:
		case actionTypes.INTERACTIVE_VIDEO_SAVE_SETTINGS_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_GET_SETTINGS_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_PREVIEW_FETCH_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_BREAKPOINT_SAVE_POSITION_ERROR:
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_CREDITS_FAILED:
		case actionTypes.VIDEO_COPY_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_EMBED_IN_SLIDE_SAVE_FAILED:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_SAVE_TRIM_FAILED:
			return openInformation(state, {
				messageObject: {
					message: localize('global_generic_error'),
					styleType: 'error',
				},
			})
		case actionTypes.INTERACTIVE_VIDEO_SAVE_FAILED:
		case actionTypes.VIDEO_SAVE_FAILED:
			if (action.error.error_code !== localErrorCodes.TRIM_CHANGE_NOT_SAVED) {
				return openInformation(state, {
					messageObject: {
						message: localize('global_generic_error'),
						styleType: 'error',
					},
				})
			} else {
				return state
			}
		case actionTypes.PRIVATE_LIBRARY_MOVE_LESSON_RELEASE:
		case actionTypes.PRIVATE_LIBRARY_MOVE_FOLDER_RELEASE:
		case actionTypes.MY_LIBRARY_MOVE_LESSON:
		case actionTypes.MY_LIBRARY_MOVE_FOLDER:
			return openInformation(state, {
				messageObject: {
					message: `"${action.sourceName}" ${localize('admin_private_library_entityMoved')} "${action.targetName}"`,
					styleType: 'success',
				},
			})
		case actionTypes.POLL_TIMER_SAVE_ERROR:
			return openInformation(state, {
				messageObject: {
					message: localize('timer_save_error'),
					styleType: 'error',
				},
			})
		case actionTypes.ADMIN_ONBOARDING_PREMIUM_ACTIVITIES_SHOW_MY_LIBRARY:
			return openInformation(state, {
				messageObject: {
					message: localize('admin_onboarding_premium_activities_success_message'),
					styleType: 'success',
				},
			})
		case actionTypes.SCHOOL_INFORMATION_SAVED:
			if (action.message === '') {
				return state
			}

			return openInformation(state, {
				messageObject: {
					message: action.message,
					timeout: 3000,
					styleType: 'success',
				},
			})
		case actionTypes.DM_STORAGE_EXPERIMENT_REFER_CONTACT_SEND_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('dm_storage_experiment_space_doubled'),
					styleType: 'success',
				},
			})
		case actionTypes.MY_ACCOUNT_SAVE_SCHOOL_INFO_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('user_profile_save_success'),
					styleType: 'success',
				},
			})
		case actionTypes.MY_ACCOUNT_SAVE_SCHOOL_INFO_FAILED:
			return openInformation(state, {
				messageObject: {
					message: action.message,
					styleType: 'error',
				},
			})
		case actionTypes.MY_LIBRARY_FIRST_LESSON_FILE_UPLOADED_ERROR:
			return openInformation(state, {
				messageObject: {
					message: action.serverError.message,
					styleType: 'error',
				},
			})
		case actionTypes.MY_LIBRARY_FIRST_LESSON_COMPLETED:
			return openInformation(state, {
				messageObject: {
					message: localize('first_lesson_overlay_upload_success'),
					styleType: 'success',
				},
			})
		case actionTypes.MY_LIBRARY_CREATE_GOOGLE_DRIVE_LESSON_FAILED:
			if (action.error.error_code === localErrorCodes.GOOGLE_SLIDES_UNAUTHORIZED_USER) {
				return state
			} else {
				return openInformation(state, {
					messageObject: {
						message: localize('global_generic_error'),
						styleType: 'error',
					},
				})
			}
		case actionTypes.QUICK_LAUNCH_TIMER_CANCEL_PILL:
			return openInformation(state, {
				messageObject: {
					message: localize('quick_launch_timer_confirm_cancel_modal_toast'),
					styleType: 'error',
				},
			})
		case actionTypes.QUICK_LAUNCH_TIMER_RESTART:
			return openInformation(state, {
				messageObject: {
					message: localize('quick_launch_timer_confirm_restart_modal_toast'),
					styleType: 'success',
				},
			})
		case actionTypes.QUICK_LAUNCH_TIMER_PAUSE:
			return closeLastOpenInformation(state, {
				messageObject: {
					message: localize('quick_launch_timer_pause_time_toast'),
					styleType: 'neutral',
				},
			})
		case actionTypes.QUICK_LAUNCH_TIMER_RESUME:
			return closeLastOpenInformation(state, {
				messageObject: {
					message: localize('quick_launch_timer_resume_time_toast'),
					styleType: 'success',
				},
			})
		case actionTypes.QUICK_LAUNCH_TIMER_ADD_TIME:
			return closeLastOpenInformation(state, {
				messageObject: {
					message: localize('quick_launch_timer_add_time_toast'),
					styleType: 'success',
				},
			})
		case actionTypes.QUICK_LAUNCH_TIMER_REMOVE_TIME:
			return closeLastOpenInformation(state, {
				messageObject: {
					message: localize('quick_launch_timer_remove_time_toast'),
					styleType: 'error',
				},
			})
		case actionTypes.GOOGLE_SLIDES_FORGOT_PASSWORD:
			return showForgotPasswordInformation(state, action)
		case actionTypes.NAVIGATION_MENU_NOTIFICATION_PREFERENCES_EMAIL_NOTIFICATION_ENABLED_FAILED:
			return closeLastOpenInformation(state, {
				messageObject: {
					message: localize('global_generic_error'),
					styleType: 'error',
					timeout: 3000,
				},
			})
		case actionTypes.NAVIGATION_MENU_NOTIFICATION_PREFERENCES_EMAIL_NOTIFICATION_ENABLED_SUCCESS:
			return closeLastOpenInformation(state, {
				messageObject: {
					message: localize('navigationMenu_notificationPreferences_updateEmailNotification_success'),
					styleType: 'success',
					timeout: 3000,
				},
			})
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_STATUS_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('interactive_video_upload_success'),
					styleType: 'success',
				},
			})
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_CREDITS_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('interactive_video_upload_credits_success'),
					styleType: 'success',
				},
			})
		case actionTypes.INTERACTIVE_VIDEO_CREATION_OEQ_SAVE_SUCCESS:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_SAVE_SUCCESS:
		{
			const message = action.isEdition
				? localize('interactive_video_activity_updated')
				: localize('interactive_video_activity_added')
			return openInformation(state, {
				messageObject: {
					message,
					styleType: 'success',
				},
			})
		}
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('interactive_video_activity_deleted'),
					styleType: 'error',
				},
			})
		case actionTypes.USER_SCHOOL_PROFILE_SAVE_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('user_profile_save_success'),
					styleType: 'success',
					timeout: 3000,
				},
			})
		case actionTypes.INTERACTIVE_VIDEO_CREATION_OEQ_SAVE_ERROR:
			return openInformation(state, {
				messageObject: {
					actionType: action.type,
					message: action.error.message || localize('global_generic_error'),
					styleType: 'error',
				},
			})
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_SAVE_SUCCESS:
			if (action.videoStatus !== videoStatusTypes.SAVING) {
				return openInformation(state, {
					messageObject: {
						message: localize('interactive_video_settings_updated_banner'),
						styleType: 'success',
					},
				})
			}
			return state
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_SAVE_FAILED:
		{
			const validationErrors = [
				localErrorCodes.VALIDATION_ERROR,
				errorCodes.LESSON_DUPLICATED_NAME,
				errorCodes.LESSON_UNTITLED,
			]
			return openInformation(state, {
				messageObject: {
					message: validationErrors.includes(action.error.error_code) ?
						localize('global_complete_fields') :
						localize('global_generic_error'),
					styleType: 'error',
				},
			})
		}
		case actionTypes.SIGNUP_TEACHER_EMAIL_ALREADY_REGISTERED:
			return openInformation(state, {
				messageObject: {
					message: localize('signup_emailAlreadyRegistered_error'),
					styleType: 'success',
				},
			})
		case actionTypes.GOOGLE_SLIDES_SAVE_PRESENTATION_PROGRESSIVE_START_FAILED:
			return handleGoogleSlidesSaveFailed(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_SAVE_TRIM_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('interactive_video_trimming_video_trimed'),
					styleType: 'success',
				},
			})
		case actionTypes.USER_ACCEPT_TERMS_AND_CONDITIONS_SUCCESS:
			return openInformation(state, {
				messageObject: {
					message: localize('terms_and_conditions_success'),
					styleType: 'success',
				},
			})
		case actionTypes.USER_ACCEPT_TERMS_AND_CONDITIONS_ERROR:
			return openInformation(state, {
				messageObject: {
					message: localize('terms_and_conditions_error'),
					styleType: 'error',
				},
			})
		default:
			return state
	}
}

export default informationReducer
